/**
 * The manager's week.
 *
 * The book's framing is that a timesheet is written by one person and read by
 * another, and the reading screen answers two questions: who has not submitted,
 * and where the hours went. Both are aggregates over one week's sheets, so the
 * arithmetic lives here rather than inside the screen.
 *
 * Every week boundary is a UTC calendar day. A week derived from local `Date`
 * fields puts a Sunday-night sheet into the next week for anyone east of UTC.
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_MS = 86400000;

/** The UTC calendar day of a timestamp, as "YYYY-MM-DD". */
export const dayKey = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

/**
 * The Monday of the week a day falls in. Weeks start Monday: the book's grid
 * runs Mon–Fri with the weekend at the end, and a Sunday-start week would put
 * the two quiet days at opposite ends of the row.
 */
export const weekStart = (value) => {
  const key = dayKey(value);
  if (!key) return "";
  const t = Date.parse(`${key}T00:00:00.000Z`);
  if (Number.isNaN(t)) return "";
  const dow = new Date(t).getUTCDay(); // 0 = Sunday
  const back = (dow + 6) % 7;
  return new Date(t - back * DAY_MS).toISOString().slice(0, 10);
};

/** "Week of 16 March" — spelled from a table, because ICU renders "Sept". */
export const weekLabel = (key) => {
  const k = dayKey(key);
  if (!k) return "";
  const [, m, d] = k.split("-");
  const i = Number(m) - 1;
  if (i < 0 || i > 11) return "";
  return `Week of ${Number(d)} ${MONTHS[i]}`;
};

/** The week keys present in a set of sheets, most recent first. */
export const weeksIn = (sheets = []) => {
  const keys = new Set();
  sheets.forEach((s) => {
    const k = weekStart(s?.startDate);
    if (k) keys.add(k);
  });
  return [...keys].sort().reverse();
};

export const inWeek = (sheet, week) => weekStart(sheet?.startDate) === week;

/** Hours are one decimal everywhere, because a week is summed by eye. */
export const hours = (n) => `${(Number(n) || 0).toFixed(1)}`;
export const hoursLabel = (n) => `${Math.round(Number(n) || 0).toLocaleString("en-IN")}h`;

const sum = (rows, pick) => rows.reduce((t, r) => t + (Number(pick(r)) || 0), 0);

/**
 * A sheet counts as submitted once it has left the employee's hands. The status
 * enum is incomplete → pending → approved/rejected, so anything past
 * "incomplete" has been submitted; a rejected sheet was still submitted, and
 * counting only pending would make the submission rate fall as the manager
 * works through the queue.
 */
export const isSubmitted = (s) => s?.status && s.status !== "incomplete";
export const isAwaiting = (s) => s?.status === "pending";
export const isDecided = (s) => s?.status === "approved" || s?.status === "rejected";

/**
 * Billable hours, from the activities rather than the rollup: `billableAmount`
 * is money, and a rate of zero would make billable hours vanish.
 */
export const billableHours = (sheet) =>
  (sheet?.activities || []).reduce(
    (t, a) => t + (a?.billable === false ? 0 : Number(a?.hours) || 0),
    0
  );

export const activityHours = (sheet) =>
  (sheet?.activities || []).reduce((t, a) => t + (Number(a?.hours) || 0), 0);

/**
 * The six counters, minus the one that cannot be computed.
 *
 * The book's sixth is "Submitted late · after Friday 18:00". There is no
 * submission timestamp on a timesheet — the status moves to `pending` and
 * nothing records when — and `updatedAt` also moves on approval, so using it
 * would count the manager's own actions as late submissions. It is omitted
 * rather than approximated; see `MISSING_COUNTERS`.
 *
 * `headcount` is the roster, so "118 of 142" can name the people who have not
 * submitted at all — they have no sheet, so they cannot be counted from sheets.
 */
export const MISSING_COUNTERS = [
  { key: "late", label: "Submitted late", why: "no timesheet records when it was submitted" },
];

export const summarise = (sheets = [], { headcount = 0, weeklyRegularHours = 40 } = {}) => {
  const submitted = sheets.filter(isSubmitted);
  const awaiting = sheets.filter(isAwaiting);
  const decided = sheets.filter(isDecided);

  const logged = sum(submitted, (s) => s.totalHours);
  const billable = submitted.reduce((t, s) => t + billableHours(s), 0);
  const overtime = sum(submitted, (s) => s.overtimeHours);
  const overCount = submitted.filter(
    (s) => (Number(s.overtimeHours) || 0) > 0 || (Number(s.totalHours) || 0) > weeklyRegularHours
  ).length;

  return {
    submitted: {
      count: submitted.length,
      of: headcount,
      outstanding: Math.max(0, headcount - submitted.length),
    },
    awaiting: { count: awaiting.length, decided: decided.length },
    logged: { hours: logged, sheets: submitted.length },
    overtime: { hours: overtime, people: overCount, threshold: weeklyRegularHours },
    billable: {
      hours: billable,
      of: logged,
      // A week with no hours has no billable share; 0% would read as "nothing
      // was billable" rather than "nothing was logged".
      share: logged > 0 ? billable / logged : null,
    },
  };
};

/**
 * Where the hours went, by project.
 *
 * The book: "The 4,912 hours in the breakdown are the 118 submitted sheets only
 * — an unsubmitted week contributes nothing." A draft is not a record of work,
 * and letting drafts into the breakdown makes the total disagree with the
 * counter directly above it.
 */
export const projectShare = (sheets = [], { top = 4 } = {}) => {
  const submitted = sheets.filter(isSubmitted);
  // Keyed on the project reference where there is one, so two spellings of the
  // same project stop reading as two projects. The label is carried alongside
  // for display, taking whichever the activity recorded.
  const byProject = new Map();
  submitted.forEach((s) => {
    (s.activities || []).forEach((a) => {
      const ref = a?.projectRef?._id || a?.projectRef;
      const name = a?.projectRef?.name || a?.project || "Internal and admin";
      const key = ref ? `ref:${String(ref)}` : `name:${name}`;
      const prev = byProject.get(key) || { name, hours: 0 };
      byProject.set(key, { name: prev.name || name, hours: prev.hours + (Number(a?.hours) || 0) });
    });
  });

  const sorted = [...byProject.values()]
    .map(({ name, hours: h }) => ({ name, hours: h }))
    .sort((a, b) => b.hours - a.hours);

  const head = sorted.slice(0, top);
  const rest = sorted.slice(top).reduce((t, p) => t + p.hours, 0);
  const shown = rest > 0 ? [...head, { name: "Other projects", hours: rest }] : head;
  const total = sorted.reduce((t, p) => t + p.hours, 0);

  return {
    total,
    rows: shown.map((p, i) => ({
      ...p,
      share: total ? p.hours / total : 0,
      // Projects keep their palette colour between the bar and the table, the
      // same rule as Reporting and Expenses.
      colorIndex: i,
    })),
  };
};

/**
 * One row of the queue, including what the manager can do with it.
 *
 * A person with no sheet is a row too — the book names the outstanding rather
 * than leaving them as a number in a counter — so this builds rows from the
 * roster, not from the sheets.
 */
export const buildRows = (sheets = [], roster = [], week, departments = []) => {
  // Employee.department is a String holding a department id, so `.name` is
  // never set on it — the old `e.department?.name || e.department` fell
  // through and printed the raw ObjectId in the Team column. Looked up
  // instead, and an id that resolves to nothing shows a dash: a dangling
  // reference is missing data, and an id is not a team name.
  const teamName = (value) => {
    if (!value) return "—";
    if (typeof value === "object") return value.name || "—";
    return departments.find((d) => String(d?._id) === String(value))?.name || "—";
  };

  const byEmployee = new Map();
  sheets.filter((s) => inWeek(s, week)).forEach((s) => {
    const id = String(s.employee?._id || s.employee || "");
    // Keep the furthest-along sheet if somehow there are two for one week.
    const existing = byEmployee.get(id);
    if (!existing || (isSubmitted(s) && !isSubmitted(existing))) byEmployee.set(id, s);
  });

  return roster.map((e) => {
    const sheet = byEmployee.get(String(e._id)) || null;
    const status = sheet?.status || "not_submitted";
    return {
      _id: sheet?._id || `none-${e._id}`,
      sheetId: sheet?._id || null,
      employee: e,
      employeeName: `${e.fname || ""} ${e.lname || ""}`.trim() || "Unknown",
      team: teamName(e.department),
      status,
      totalHours: sheet ? Number(sheet.totalHours) || 0 : null,
      overtimeHours: sheet ? Number(sheet.overtimeHours) || 0 : null,
      sheet,
    };
  });
};

export const ROW_STATUS = {
  not_submitted: { label: "Not submitted", tone: "muted", action: "remind" },
  incomplete: { label: "In progress", tone: "muted", action: "none" },
  pending: { label: "Submitted", tone: "warning", action: "approve" },
  approved: { label: "Approved", tone: "success", action: "view" },
  rejected: { label: "Rejected", tone: "danger", action: "view" },
};

export const rowStatus = (row) => ROW_STATUS[row?.status] || ROW_STATUS.not_submitted;

/** Filter buckets for the queue, each one a real question a manager asks. */
export const BUCKETS = {
  awaiting: (r) => r.status === "pending",
  outstanding: (r) => r.status === "not_submitted" || r.status === "incomplete",
  overtime: (r) => (r.overtimeHours || 0) > 0,
  approved: (r) => r.status === "approved",
  all: () => true,
};

export const selectRows = (rows = [], bucket = "all", search = "") => {
  const predicate = BUCKETS[bucket] || BUCKETS.all;
  const q = String(search || "").trim().toLowerCase();
  return rows.filter(
    (r) => predicate(r) && (!q || r.employeeName.toLowerCase().includes(q) || String(r.team).toLowerCase().includes(q))
  );
};
